import { useRef, useState } from 'react';
import { productCategories } from '@/mocks/products';

export default function ProductShowcase() {
  return (
    <section className="py-12 md:py-16 bg-white">
      <div className="px-4 md:px-6">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-10">
            <span className="inline-block text-xs font-semibold tracking-widest text-primary-600 uppercase mb-2">
              Featured Products
            </span>
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-3">
              CSSBuy Spreadsheet Products
            </h2>
            <p className="text-sm text-foreground-600 max-w-xl mx-auto">
              Browse our curated selection of products from the CSSBuy Spreadsheet.
              Click any item to view details and purchase options.
            </p>
          </div>

          {productCategories.map((cat) => (
            <CategoryRow key={cat.key} category={cat} />
          ))}
        </div>
      </div>
    </section>
  );
}

function CategoryRow({ category }: { category: typeof productCategories[0] }) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);

  const checkScroll = () => {
    const el = scrollRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 10);
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 10);
  };

  const scroll = (dir: number) => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollBy({ left: dir * 280, behavior: 'smooth' });
    setTimeout(checkScroll, 300);
  };

  return (
    <div className="mb-10 md:mb-14">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-heading text-lg md:text-xl font-bold text-foreground-900">
          {category.label}
        </h3>
        <a
          href={category.categoryUrl}
          target="_blank"
          rel="noopener nofollow"
          className="text-xs md:text-sm font-medium text-primary-600 hover:text-primary-700 flex items-center gap-1 cursor-pointer"
        >
          View All
          <i className="ri-arrow-right-line" />
        </a>
      </div>

      <div className="relative group">
        {canScrollLeft && (
          <button
            onClick={() => scroll(-1)}
            className="absolute left-0 top-1/2 -translate-y-1/2 z-10 w-8 h-8 md:w-10 md:h-10 rounded-full bg-white shadow-lg border border-background-200 flex items-center justify-center text-foreground-600 hover:text-primary-600 transition-colors cursor-pointer"
            aria-label="Scroll left"
          >
            <i className="ri-arrow-left-s-line text-lg" />
          </button>
        )}

        <div
          ref={scrollRef}
          onScroll={checkScroll}
          className="flex gap-4 overflow-x-auto pb-4 snap-x snap-mandatory scrollbar-hide"
          style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
        >
          {category.products.map((product) => (
            <a
              key={product.id}
              href={product.link}
              target="_blank"
              rel="noopener nofollow"
              className="flex-shrink-0 w-44 md:w-56 snap-start group/card cursor-pointer"
            >
              <div className="bg-background-50 rounded-xl border border-background-200/70 overflow-hidden hover:shadow-lg hover:border-primary-200 transition-all duration-300">
                <div className="w-full h-44 md:h-56 bg-white overflow-hidden">
                  <img
                    src={product.image}
                    alt={product.name}
                    className="w-full h-full object-cover group-hover/card:scale-105 transition-transform duration-300"
                    loading="lazy"
                    onError={(e) => {
                      const target = e.target as HTMLImageElement;
                      target.style.display = 'none';
                      const parent = target.parentElement;
                      if (parent) {
                        parent.innerHTML = '<div class="w-full h-full flex items-center justify-center text-foreground-300"><i class="ri-image-line text-2xl"></i></div>';
                      }
                    }}
                  />
                </div>
                <div className="p-3 md:p-4">
                  <h4 className="text-xs md:text-sm font-medium text-foreground-800 leading-snug line-clamp-2 mb-2 min-h-[2.5rem]">
                    {product.name}
                  </h4>
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-bold text-primary-600">
                      {product.price}
                    </span>
                    <span className="text-xs text-foreground-400 bg-primary-50 px-2 py-1 rounded-md font-medium">
                      Buy Now
                    </span>
                  </div>
                </div>
              </div>
            </a>
          ))}
        </div>

        {canScrollRight && (
          <button
            onClick={() => scroll(1)}
            className="absolute right-0 top-1/2 -translate-y-1/2 z-10 w-8 h-8 md:w-10 md:h-10 rounded-full bg-white shadow-lg border border-background-200 flex items-center justify-center text-foreground-600 hover:text-primary-600 transition-colors cursor-pointer"
            aria-label="Scroll right"
          >
            <i className="ri-arrow-right-s-line text-lg" />
          </button>
        )}
      </div>
    </div>
  );
}